import { useContentStore } from "@/stores/content";
import { Channel } from "./channel";
import { defaultPropertyStep } from "./hardCodedValues";
import { ChannelPoint, ChannelPort, ChannelPortWithSize, ChannelSegment, ContentUnitGeneral } from "./other";
import { ComponentObject, Valveoutput } from "./typeDefinations";
import { PayloadChannel, PayloadCircle, PayloadLine, PayloadShape, Point } from "./payloadPrimitives";
import { reactive, watch } from 'vue';
import DelayChannel from "./modules/delayChannel";
import TeslaValve from "./modules/teslaValve";
import ComponentPolygon from "./componentPolygon";
import { useControlStore } from "@/stores/control";
const dummyChannelPoint = new ChannelPoint([0, 0]);

export class Valve extends ContentUnitGeneral {

  id = "";
  channelSegment: ChannelSegment;
  posOnSeg: number; // 0 <= value <= 1, indecate the relative position on the channel segment
  properties = {
    height: {
      value: 400,
      name: "Height",
      step: defaultPropertyStep,
      unit: "µm"
    },
    length: {
      value: 800,
      name: "Length",
      step: defaultPropertyStep,
      unit: "µm"
    },
    width: {
      value: 1600,
      name: "Width",
      step: defaultPropertyStep,
      unit: "µm"
    },
    roundedRadius: {
      value: 200,
      name: "Radius",
      step: defaultPropertyStep,
      unit: "µm"
    }
  }
  channelPorts = Array<ChannelPort | ChannelPortWithSize>();

  static parse(content: Valveoutput): Valve {
    const contentStore = useContentStore();
    const channel = contentStore.getElement(content.connectedChannel);
    const start = channel.channelDef[content.startPointIdx];
    const end = channel.channelDef[content.startPointIdx + 1];
    const channelSegment = {
      cid: content.connectedChannel,
      idx: content.startPointIdx,
      start,
      end,
      width: channel.properties.channelWidth.value
    };
    const res = new Valve(channelSegment, content.position, content.layer, content.properties);
    res.id = content.id;
    return res;
  }

  static resetCount(): void {
    Valve.count = 0;
  }

  getHeight(): number {
    const contentStore = useContentStore();
    const c = contentStore.getElement(this.channelSegment.cid);
    return (
      this.properties.height.value + (c instanceof Channel ? c.getHeight() : 0)
    );
  }

  buildOutput(layer: string): Valveoutput {
    const properties = new Map();
    for (const [key, value] of Object.entries(this.properties)) {
      properties.set(key, value.value);
    }
    const output = {
      id: this.id,
      position: this.posOnSeg,
      connectedChannel: this.channelSegment.cid,
      startPointIdx: this.channelSegment.idx,
      properties: Object.fromEntries(properties),
      layer
    };
    return output;
  }

  initializeChannelPortsWithoutSize(): void {
    for (let i = 0; i < this.numberOfChannelPorts; i++) {
      this.channelPorts.push(new ChannelPort(0, 0, this.id, i));
    }
  }

  show = true;

  get type(): string {
    return "valve";
  }

  get vueComponent(): string {
    return "SvgValveVue";
  }
  get display(): boolean {
    const contentStore = useContentStore();
    const c = contentStore.getElement(this.channelSegment.cid);
    return (c instanceof Channel || c instanceof ComponentPolygon || c instanceof DelayChannel || c instanceof TeslaValve) && c.display && this.show;
  }

  get numberOfChannelPorts(): number {
    return 4;
  }

  updatePort(): void {
    if (this.channelPorts.length === this.numberOfChannelPorts) {
      this.updatePortHelper();
    }
  }

  updatePortHelper(): void {
    const channelStart = [this.channelSegment.start.position[0], this.channelSegment.start.position[1]];
    const channelEnd = [this.channelSegment.end.position[0], this.channelSegment.end.position[1]];
    const direction = [channelEnd[0] - channelStart[0], channelEnd[1] - channelStart[1]];
    const magnitudeDir = Math.sqrt(direction[0] * direction[0] + direction[1] * direction[1]);
    const directionNorm = [direction[0] / magnitudeDir, direction[1] / magnitudeDir];
    const perpendicularNorm = [-directionNorm[1], directionNorm[0]];

    const center = [channelStart[0] + this.posOnSeg * direction[0], channelStart[1] + this.posOnSeg * direction[1]];
    const channelPoint1 = [center[0] - 0.5 * this.properties.width.value * perpendicularNorm[0], 
                          center[1] - 0.5 * this.properties.width.value * perpendicularNorm[1]];
    const channelPoint2 = [center[0] + 0.5 * this.properties.width.value * perpendicularNorm[0],
                          center[1] + 0.5 * this.properties.width.value * perpendicularNorm[1]];
    const channelPoint3 = [center[0] - 0.5 * this.properties.length.value * directionNorm[0], 
                          center[1] - 0.5 * this.properties.length.value * directionNorm[1]];
    const channelPoint4 = [center[0] + 0.5 * this.properties.length.value * directionNorm[0],
                          center[1] + 0.5 * this.properties.length.value * directionNorm[1]];
                          
    this.channelPorts[0].position[0] = channelPoint1[0];
    this.channelPorts[0].position[1] = channelPoint1[1];
    this.channelPorts[1].position[0] = channelPoint2[0];
    this.channelPorts[1].position[1] = channelPoint2[1];
    this.channelPorts[2].position[0] = channelPoint3[0];
    this.channelPorts[2].position[1] = channelPoint3[1];
    this.channelPorts[3].position[0] = channelPoint4[0];
    this.channelPorts[3].position[1] = channelPoint4[1];
  }

  static count = 0;
  constructor(cs: ChannelSegment, pos: number, layer: string, properties?: { height: number, length: number, width: number, roundedRadius: number}) {
    super(layer);
    this.channelSegment = cs;
    this.posOnSeg = pos;
    Valve.count++;
    const contentStore = useContentStore();
    const controlStore =useControlStore();
    while (contentStore.contentMap.has("Valve-" + Valve.count)) {
      Valve.count++;
    }
    this.id = "Valve-" + Valve.count;
    // if valve on same layer as channel, set width and length
    if (properties) {
      this.properties.height.value = properties.height;
      this.properties.length.value = properties.length;
      this.properties.width.value = properties.width;
      this.properties.roundedRadius.value = properties.roundedRadius;
    } else if (contentStore.getElement(this.channelSegment.cid).layer === contentStore.crtLayer && !properties) {
      this.properties.height.value = controlStore.defaultValveHeight;
      this.properties.length.value = controlStore.defaultValveWidth;
      this.properties.width.value = controlStore.defaultValveLength;
      this.properties.roundedRadius.value = controlStore.defaultValveRoundedRadius;
    } else {
      this.properties.height.value = controlStore.defaultValveHeight;
      this.properties.length.value = controlStore.defaultValveLength;
      this.properties.width.value = controlStore.defaultValveWidth;
      this.properties.roundedRadius.value = controlStore.defaultValveRoundedRadius;
    }
    
    this.properties = reactive(this.properties);
    this.setupWatchers();
    this.initializeChannelPortsWithoutSize();
    this.updatePort();
  }

  buildPayload(z: number): ComponentObject {
    if (this.properties.height.value <= 0 || this.properties.length.value <= 0 || this.properties.width.value <= 0) {
      throw "invalid valve properties at " + this.id;
    }
    const comp = {
      id: this.id,
      shapes: Array<PayloadShape>(),
      channels: Array<PayloadChannel>(),
      ports: Array<string>()
    };
    const channelStart = [this.channelSegment.start.position[0], this.channelSegment.start.position[1], z];
    const channelEnd = [this.channelSegment.end.position[0], this.channelSegment.end.position[1], z];
    const direction = [channelEnd[0] - channelStart[0], channelEnd[1] - channelStart[1], channelEnd[2] - channelStart[2]];
    const magnitudeDir = Math.sqrt(direction[0] * direction[0] + direction[1] * direction[1] + direction[2] * direction[2]);
    const directionNorm = [direction[0] / magnitudeDir, direction[1] / magnitudeDir, direction[2] / magnitudeDir];
    const perpendicularNorm = [-directionNorm[1], directionNorm[0], 0];

    const contentStore = useContentStore();

    // center of valve
    const center = [channelStart[0] + this.posOnSeg * direction[0], channelStart[1] + this.posOnSeg * direction[1], channelStart[2] + this.posOnSeg * direction[2]];
    // get start and end points
    const startPoint1 = new Point(center[0] - 0.5 * this.properties.length.value * directionNorm[0], center[1] - 0.5 * this.properties.length.value * directionNorm[1], z + 0.5 * this.properties.height.value, contentStore.chipLengthY);
    const endPoint1 = new Point(center[0] + 0.5 * this.properties.length.value * directionNorm[0], center[1] + 0.5 * this.properties.length.value * directionNorm[1], z + 0.5 * this.properties.height.value, contentStore.chipLengthY);
    const startPoint2 = new Point(center[0] - (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[0], center[1] - (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[1], z + 0.5 * this.properties.height.value, contentStore.chipLengthY);
    const endPoint2 = new Point(center[0] + (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[0], center[1] + (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[1], z + 0.5 * this.properties.height.value, contentStore.chipLengthY);
    const minVal = Math.min(this.properties.width.value, this.properties.length.value);

    if (this.properties.roundedRadius.value == 0) {
      if (2 * this.properties.roundedRadius.value > minVal) {
        const shapes = [
          new PayloadLine(startPoint1, endPoint1, this.properties.width.value - this.properties.roundedRadius.value * 2, this.properties.height.value)
        ];
        shapes.forEach((s) => comp.shapes.push(s));
      } else {
        const shapes = [
          new PayloadLine(startPoint1, endPoint1, this.properties.width.value - this.properties.roundedRadius.value * 2, this.properties.height.value),
          new PayloadLine(startPoint2, endPoint2, this.properties.width.value, this.properties.height.value),
        ];
        shapes.forEach((s) => comp.shapes.push(s));
      }
      return comp;
    } else {
      const circleCenter1 = new Point(
        center[0] - (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[0] + (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[0], 
        center[1] - (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[1] + (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[1], 
        z, contentStore.chipLengthY
      );
      const circleCenter2 = new Point(
        center[0] + (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[0] + (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[0], 
        center[1] + (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[1] + (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[1], 
        z, contentStore.chipLengthY
      );
      const circleCenter3 = new Point(
        center[0] + (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[0] - (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[0], 
        center[1] + (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[1] - (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[1], 
        z, contentStore.chipLengthY
      );
      const circleCenter4 = new Point(
        center[0] - (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[0] - (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[0], 
        center[1] - (0.5 * this.properties.length.value - this.properties.roundedRadius.value) * directionNorm[1] - (0.5 * this.properties.width.value - this.properties.roundedRadius.value) * perpendicularNorm[1], 
        z, contentStore.chipLengthY
      );
    
      if (2 * this.properties.roundedRadius.value > minVal) {
        const shapes = [
          new PayloadLine(startPoint1, endPoint1, this.properties.width.value - this.properties.roundedRadius.value * 2, this.properties.height.value),
          new PayloadCircle(circleCenter1, true, this.properties.height.value, this.properties.roundedRadius.value),
          new PayloadCircle(circleCenter2, true, this.properties.height.value, this.properties.roundedRadius.value),
          new PayloadCircle(circleCenter3, true, this.properties.height.value, this.properties.roundedRadius.value),
          new PayloadCircle(circleCenter4, true, this.properties.height.value, this.properties.roundedRadius.value)
        ];
        shapes.forEach((s) => comp.shapes.push(s));
      } else {
        const shapes = [
          new PayloadLine(startPoint1, endPoint1, this.properties.width.value - this.properties.roundedRadius.value * 2, this.properties.height.value),
          new PayloadLine(startPoint2, endPoint2, this.properties.width.value, this.properties.height.value),
          new PayloadCircle(circleCenter1, true, this.properties.height.value, this.properties.roundedRadius.value),
          new PayloadCircle(circleCenter2, true, this.properties.height.value, this.properties.roundedRadius.value),
          new PayloadCircle(circleCenter3, true, this.properties.height.value, this.properties.roundedRadius.value),
          new PayloadCircle(circleCenter4, true, this.properties.height.value, this.properties.roundedRadius.value)
        ];
        shapes.forEach((s) => comp.shapes.push(s));
      }
      return comp;
    }
  }

  setupWatchers(): void {
    watch(() => this.properties, () => {
      const minVal = Math.min(this.properties.width.value, this.properties.length.value);
      if (2 * this.properties.roundedRadius.value > minVal) {
        this.properties.roundedRadius.value = minVal / 2;
      }
    }, {
      deep: true 
    });
  }

  makeCopy(
    start: ChannelPoint,
    cid: string,
    width: number,
    idx: number
  ): Valve {
    const contentStore = useContentStore();
    const res = new Valve(
      { start, cid, width, end: dummyChannelPoint, idx },
      this.posOnSeg,
      this.layer,
      {
        height: this.properties.height.value,
        length: this.properties.length.value,
        width: this.properties.width.value,
        roundedRadius: this.properties.roundedRadius.value
      }
    );
    contentStore.valveIds.push(res.id);
    return res;
  }
}